import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { apiClient } from '../../services/api';
import EmptyState from '../shared/EmptyState';
import './QualityTrendChart.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface TrendPoint { date: string; pass: number; fail: number; blocked: number; }

const RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

export const QualityTrendChart: React.FC = () => {
  const [rangeDays, setRangeDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [comparisons, setComparisons] = useState<{ volume_prior: number; volume_delta: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/api/v1/dashboard/stats?days=${rangeDays}`)
      .then(resp => {
        if (cancelled) return;
        const data = resp.data as any;
        setTrend(data.execution_trend || []);
        setComparisons(data.comparisons || null);
      })
      .catch(() => { if (!cancelled) { setTrend([]); setComparisons(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rangeDays]);

  const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const chartData = {
    labels: trend.map(t => fmtDate(t.date)),
    datasets: [
      {
        label: 'Passed',
        data: trend.map(t => t.pass),
        borderColor: '#16a34a',
        backgroundColor: 'rgba(22, 163, 74, 0.08)',
        tension: 0.35,
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Failed',
        data: trend.map(t => t.fail),
        borderColor: '#dc2626',
        backgroundColor: 'rgba(220, 38, 38, 0.06)',
        tension: 0.35,
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Blocked',
        data: trend.map(t => t.blocked),
        borderColor: '#d97706',
        backgroundColor: 'rgba(217, 119, 6, 0.06)',
        tension: 0.35,
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Volume',
        data: trend.map(t => t.pass + t.fail + t.blocked),
        borderColor: '#0066cc',
        backgroundColor: 'transparent',
        borderDash: [5, 4],
        tension: 0.35,
        fill: false,
        pointRadius: 0,
        borderWidth: 1.5,
      },
    ],
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: { boxWidth: 10, font: { size: 11 }, color: '#4b5568' },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#9099a8', font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: '#f1f2f4' }, ticks: { color: '#9099a8', font: { size: 10 } } },
    },
  };

  const volumeDelta = comparisons?.volume_delta ?? null;

  return (
    <div className="qtc-card">
      <div className="qtc-header">
        <div>
          <h3>Quality &amp; Execution Trend</h3>
          <p>Daily pass / fail / blocked, with total volume overlay</p>
        </div>
        <div className="qtc-controls">
          {comparisons && (
            <span className="qtc-vs-prior">
              vs. prior period: {volumeDelta != null && volumeDelta >= 0 ? '+' : ''}{volumeDelta ?? 0} executions
            </span>
          )}
          <div className="qtc-range-toggle">
            {RANGES.map(r => (
              <button
                key={r.label}
                className={rangeDays === r.days ? 'active' : ''}
                onClick={() => setRangeDays(r.days)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="qtc-skeleton" />
      ) : trend.length < 2 ? (
        <EmptyState message="No execution data yet." actionLabel="Create a test run" actionLink="/test-runs/create" />
      ) : (
        <div className="qtc-chart-wrap">
          <Line data={chartData} options={chartOptions} />
        </div>
      )}

      <div className="qtc-footer">
        <Link to="/test-runs">View Execution Details →</Link>
      </div>
    </div>
  );
};

export default QualityTrendChart;
